import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { UserContext } from '@/components/App';
import {
    REINVITE_CARRIER,
    SET_CARRIER_INTEGRATIONS_REQUIRED,
    SET_SHIPPER_INTEGRATIONS_REQUIRED,
} from '@/constants/apiRoutes';
import { useClientUser } from '@/hooks';
import { post } from '@/utilities/onwardClient';

import { CorpAdminContext } from '../store';
import { withParams } from '../utils';
import { BADGES, BUCKETS, SHIPPER_BADGES, SHIPPER_BUCKETS } from './constants';
import { GET_ORG_CARRIER_ONBOARDING, GET_ORG_SHIPPER_ONBOARDING } from './graphql';
import {
    bucketCounts,
    bucketForParam,
    bucketParam,
    filterByFacets,
    filterRows,
    isOnboardingCarrier,
    NO_FILTERS,
    rowsInBucket,
} from './utils';

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback;

// useOnboardingRows' default `include`, defined once so the rows memo doesn't rerun every render.
const ALL_ROWS = () => true;

/**
 * What both onboarding views share: the org's rows from `query`, the active tab, the type / badge filter and the
 * checked rows. `idField` is the row's account ID column (`carrier_id` or `shipper_id`). `include` picks the rows the
 * view lists at all; the default lists every row.
 *
 * @param {{ query: object, rowsField: string, idField: string, buckets: object[], badges: object[], search: string,
 *   include?: (row: object) => boolean }} args
 */
const useOnboardingRows = ({ query, rowsField, idField, buckets, badges, search, include = ALL_ROWS }) => {
    const { organization_id } = useClientUser();

    const { data, loading, error, refetch } = useQuery(query, {
        variables: { organization_id },
        skip: !organization_id,
        fetchPolicy: 'cache-and-network',
    });

    // The active tab is `?bucket=` in the URL, so coming back to the page reopens it.
    const [searchParams, setSearchParams] = useSearchParams();
    const bucket = bucketForParam(buckets, searchParams.get('bucket'));
    const bucketKey = bucket.key;
    const setBucketKey = useCallback(
        (key) => setSearchParams((prev) => withParams(prev, { bucket: bucketParam(key) }), { replace: true }),
        [setSearchParams]
    );
    // The type / badge filter. It applies to every tab and their counts, and stays put when the tab changes.
    const [filters, setFilters] = useState(NO_FILTERS);
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    const rows = useMemo(() => {
        const searchedRows = filterRows((data?.[rowsField] || []).filter(include), search);
        return filterByFacets(searchedRows, filters, badges);
    }, [data, rowsField, include, search, filters, badges]);
    const counts = useMemo(() => bucketCounts(rows, buckets), [rows, buckets]);
    const bucketRows = useMemo(() => rowsInBucket(rows, bucket), [rows, bucket]);
    const selectedRows = useMemo(
        () => bucketRows.filter((row) => selectedIds.has(row[idField])),
        [bucketRows, idField, selectedIds]
    );

    // A checked row stays checked only while it's on screen.
    useEffect(() => setSelectedIds(new Set()), [bucketKey, search, filters]);

    const toggleRow = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    return {
        organization_id,
        refetch,
        loading: loading && !data,
        error,
        hasOrganization: !!organization_id,
        bucket,
        setBucketKey,
        filters,
        setFilters,
        counts,
        bucketRows,
        selectedIds,
        selectedRows,
        toggleRow,
        clearSelection,
    };
};

/**
 * Sets or clears No Integrations Required on each of the checked rows through `route`, reports how it went, then
 * clears the selection and re-reads the view. The account ID goes in the body under `idField`.
 *
 * @returns {(targets: object[], integrationsRequired: boolean) => Promise<void>}
 */
const useSetIntegrationsRequired = ({ route, idField, organization_id, notify, setBusy, clearSelection, refetch }) =>
    useCallback(
        async (targets, integrationsRequired) => {
            if (!targets?.length) return;
            setBusy(true);
            // organization_id is only read by the emulator; deployed environments take it from the token.
            const results = await Promise.allSettled(
                targets.map((row) =>
                    post(route, {
                        [idField]: row[idField],
                        organization_id,
                        integrations_required: integrationsRequired,
                    })
                )
            );
            setBusy(false);

            const failed = results.filter((result) => result.status === 'rejected');
            failed.forEach((result) => Sentry.captureException(result.reason));
            if (failed.length) {
                notify?.({
                    severity: 'error',
                    message:
                        targets.length === 1
                            ? errorMessage(failed[0].reason, 'Error saving the integrations setting')
                            : `${failed.length} of ${targets.length} integrations settings failed`,
                });
            } else {
                notify?.({
                    severity: 'success',
                    message: integrationsRequired ? 'Integrations required' : 'Marked No Integrations Required',
                });
            }
            clearSelection();
            refetch();
        },
        [clearSelection, idField, notify, organization_id, refetch, route, setBusy]
    );

/**
 * Everything the carrier onboarding view renders and does: the org's rows from organization_carrier_statuses, the
 * active tab, the checked rows, and the writes behind the options panel (Invite and the integrations setting). Writes
 * go through the onward-functions corp endpoints, then re-read the view.
 *
 * @param {{ search: string }} args
 */
export const useCarrierOnboarding = ({ search }) => {
    const { user } = useContext(UserContext);
    const { callbacks } = useContext(CorpAdminContext);
    const notify = callbacks?.setNotification;

    const { organization_id, refetch, ...view } = useOnboardingRows({
        query: GET_ORG_CARRIER_ONBOARDING,
        rowsField: 'organization_carrier_statuses',
        idField: 'carrier_id',
        buckets: BUCKETS,
        badges: BADGES,
        search,
        include: isOnboardingCarrier,
    });
    const { clearSelection } = view;

    const [busy, setBusy] = useState(false);

    // `skipped` is how many checked carriers were left out: signed up already, or no email.
    const reinvite = useCallback(
        async (targets, skipped = 0) => {
            if (!targets?.length) return;
            setBusy(true);
            const inviterName = user?.business_name || user?.username || undefined;
            // organization_id is only read by the emulator; deployed environments take it from the token.
            const results = await Promise.allSettled(
                targets.map((row) =>
                    post(REINVITE_CARRIER, {
                        carrier_id: row.carrier_id,
                        organization_id,
                        inviter_name: inviterName,
                    })
                )
            );
            setBusy(false);

            const failed = results.filter((result) => result.status === 'rejected');
            failed.forEach((result) => Sentry.captureException(result.reason));
            const skippedNote = skipped ? `. ${skipped} skipped (already signed up or no email)` : '';
            if (failed.length) {
                notify?.({
                    severity: 'error',
                    message:
                        (targets.length === 1
                            ? errorMessage(failed[0].reason, 'Error sending the invite')
                            : `${failed.length} of ${targets.length} invites failed`) + skippedNote,
                });
            } else {
                notify?.({
                    severity: 'success',
                    message: (targets.length === 1 ? 'Invite sent' : `${targets.length} invites sent`) + skippedNote,
                });
            }
            clearSelection();
            refetch();
        },
        [clearSelection, notify, organization_id, refetch, user]
    );

    const setIntegrationsRequired = useSetIntegrationsRequired({
        route: SET_CARRIER_INTEGRATIONS_REQUIRED,
        idField: 'carrier_id',
        organization_id,
        notify,
        setBusy,
        clearSelection,
        refetch,
    });

    return {
        ...view,
        busy,
        reinvite,
        setIntegrationsRequired,
    };
};

/**
 * Everything the shipper onboarding view renders and does: the org's rows from organization_shipper_statuses, the
 * active tab, the checked rows, and the No Integrations Required toggle behind the options panel.
 *
 * @param {{ search: string }} args
 */
export const useShipperOnboarding = ({ search }) => {
    const { callbacks } = useContext(CorpAdminContext);
    const notify = callbacks?.setNotification;

    const { organization_id, refetch, ...view } = useOnboardingRows({
        query: GET_ORG_SHIPPER_ONBOARDING,
        rowsField: 'organization_shipper_statuses',
        idField: 'shipper_id',
        buckets: SHIPPER_BUCKETS,
        badges: SHIPPER_BADGES,
        search,
    });
    const [busy, setBusy] = useState(false);

    const setIntegrationsRequired = useSetIntegrationsRequired({
        route: SET_SHIPPER_INTEGRATIONS_REQUIRED,
        idField: 'shipper_id',
        organization_id,
        notify,
        setBusy,
        clearSelection: view.clearSelection,
        refetch,
    });

    return { ...view, busy, setIntegrationsRequired };
};
